import { h, Component } from 'preact'
import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import globals from '../../utils/globals'
import { SEARCH_TOKEN_URL } from '../../libs/config'
import {InstantSearch, Configure, Index} from 'react-instantsearch-dom'
import CustomSearchBox from './SearchBox'
import CustomCurrentRefinements from './FilterList'
import algoliasearch from 'algoliasearch/lite'
import SearchResults from './SearchResults'
import CustomHierarchicalMenu from './filters/HierarchicalMenu'
import Icon from '../assets'
import TagsList from './filters/Tags'
import TopicType from './filters/TopicType'
import qs from 'qs'
import Button from './../ui-components/Button'
import Phrase from '../../libs/translationsUtils/phrase'

interface State {
  client: algoliasearch | null,
  enableLoader: boolean,
  pageCount: number,
  searchState: SearchState,
  isFiltersOverlay: boolean,
  totalTopicsAmount: number
}

interface Props {
  defaultCdn: string,
  attachmentCdn: string,
  isGuest: boolean,
  isFederatedSearch: boolean,
  isFederatedSalesforceSearch: boolean
}

const createURL = (state: unknown) => `?${qs.stringify(state)}`

interface SearchState {
  query: string,
  refinementList?: {
    tags: Array<string>,
    content_type: Array<string>,
  },
  hierarchicalMenu?: {
    'categories.lvl0'?: string,
    'kb.categories.lvl0'?: string,
  }
}

interface StateInUrl {
  q?: string,
  tags?: Array<string>,
  content_type?: Array<string>,
  category?: string,
  kb_category?: string,
  search_type?: 'tag'
}

const searchStateToUrl = (searchState: SearchState) => {
  const location = window.location
  if (!searchState) return ''
  const stateToStoreInUrl: StateInUrl = {}
  if (searchState.query) {
    stateToStoreInUrl.q = searchState.query
  }
  if (searchState.refinementList?.tags) {
    stateToStoreInUrl.tags = searchState.refinementList.tags
  }

  if (searchState.hierarchicalMenu?.['categories.lvl0']) {
    stateToStoreInUrl.category = searchState.hierarchicalMenu['categories.lvl0']
  }

  if (searchState.hierarchicalMenu?.['kb.categories.lvl0']) {
    stateToStoreInUrl.kb_category = searchState.hierarchicalMenu['kb.categories.lvl0']
  }

  if (searchState.refinementList?.content_type) {
    stateToStoreInUrl.content_type = searchState.refinementList.content_type
  }

  return`${location.pathname}${createURL(stateToStoreInUrl)}`
}

const urlToSearchState = ({ search }: Location): SearchState => {
  const stateInUrl: StateInUrl = qs.parse(search.slice(1))
  
  if (stateInUrl.search_type === 'tag' && stateInUrl.q) {
    return {
      query: '',
      refinementList: {
        tags: [stateInUrl.q || ''],
        content_type: []
      }
    }
  }

  return {
    query: stateInUrl.q || '',
    refinementList: {
      tags: stateInUrl.tags || [],
      content_type: stateInUrl.content_type ? stateInUrl.content_type : []
    },
    hierarchicalMenu: {
      'categories.lvl0': stateInUrl.category || '',
      'kb.categories.lvl0': stateInUrl.kb_category || ''
    }
  }
}

class InstantSearchComponent extends Component<Props, State> {
  debouncedSetState: number = 0

  constructor () {
    super()
    this.state = {
      client: null,
      enableLoader: false,
      pageCount: 0,
      searchState: urlToSearchState(location),
      isFiltersOverlay: false,
      totalTopicsAmount: 0
    }
    this.updateUrl = this.updateUrl.bind(this)
    this.filtersOverlayHandler = this.filtersOverlayHandler.bind(this)
    this.totalTopicsAmount = this.totalTopicsAmount.bind(this)
  }

  communityId = globals('communityId') as string

  async componentDidMount () {
    const response = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})
    this.setState({
      client: algoliasearch(response['client_id'], response.token)
    })
  }

  updateUrl (updatedSearchState: SearchState) {
    clearTimeout(this.debouncedSetState)

    const searchState = Object.assign(
      {},
      this.state.searchState,
      updatedSearchState
    )

    this.debouncedSetState = setTimeout(() => {
      history.pushState(searchState, '', searchStateToUrl(this.state.searchState))
    }, 400)

    const pageChanged = searchStateToUrl(this.state.searchState) === searchStateToUrl(searchState)

    if (!pageChanged) {
      this.setState({ pageCount: 0 })
    }

    this.setState({
      searchState: searchState,
    })
  }

  filtersOverlayHandler () {
    this.setState({
      isFiltersOverlay: !this.state.isFiltersOverlay
    })

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  totalTopicsAmount (amount: number) {
    if (this.state.totalTopicsAmount !== amount) {
      this.setState({
        totalTopicsAmount: amount
      })
    }
  }

  render ({ defaultCdn, attachmentCdn, isGuest, isFederatedSearch, isFederatedSalesforceSearch }: Props, { client, enableLoader, isFiltersOverlay, pageCount, totalTopicsAmount }: State) {
    if (!client) return null

    return (
      <div className='instant-search-wrapper'>
        <InstantSearch
          searchClient={this.state.client}
          indexName={this.communityId}
          searchState={this.state.searchState}
          onSearchStateChange={this.updateUrl}
        >
          <Index indexName={this.communityId}>
            <Configure
              hitsPerPage={isFederatedSearch || isFederatedSalesforceSearch ? 20 : 25}
              facetingAfterDistinct={true}
              page={pageCount}
              restrictSearchableAttributes={['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']}
              analyticsTags={['destination', 'instant-search']}
            />
          </Index>
          {
            isFederatedSearch ? (
              <Index indexName={`${this.communityId}-zendesk`}>
                <Configure
                  hitsPerPage={5}
                  page={pageCount}
                  analyticsTags={['destination', 'instant-search']}
                />
              </Index>
            ) : null
          }
          {
            isFederatedSalesforceSearch ? (
              <Index indexName={`${this.communityId}-salesforce`}>
                <Configure
                  hitsPerPage={5}
                  page={pageCount}
                  analyticsTags={['destination', 'instant-search']}
                />
              </Index>
            ) : null
          }
          <div className='instant-search-input-wrapper'>
            <CustomSearchBox
              defaultCdn={defaultCdn}
              enableLoader={enableLoader}
              loaderCallback={() => this.setState({ enableLoader: true })}
            />
          </div>
          <button class='instant-search__filter' onClick={this.filtersOverlayHandler}>
            <Icon width={18} height={12} color='#6B707E' icon='filter' />
          </button>
          <div className='instant-search__results-wrapper'>
            <div class={`instant-search__results-wrapper-filter ${!isFiltersOverlay ? 'instant-search__results-wrapper-filter--hide' : ''}`}>
              <div class='instant-search__results-wrapper-filter-header'>
                <Icon icon='close' onClick={this.filtersOverlayHandler} />
                <h3 class='instant-search__results-wrapper-filter-header-heading'>Filters</h3>
                <button class='instant-search__results-wrapper-filter-header-btn'>Clear all</button>
              </div>
              <div class='instant-search__results-wrapper-filters'>
                <TopicType
                  title={<Phrase m='Common' p='advanced.search.filter.topic.type' />}
                  attribute='content_type'
                  searchable
                />
                
                <CustomHierarchicalMenu
                  title={<Phrase m='Common' p='advanced.search.filter.community.category' />}
                  attributes={[
                    'categories.lvl0',
                    'categories.lvl1'
                  ]}
                />

                <CustomHierarchicalMenu
                  title={<Phrase m='Common' p='advanced.search.filter.knowledgebase.category' />}
                  attributes={[
                    'kb.categories.lvl0',
                    'kb.categories.lvl1'
                  ]}
                />

                <TagsList
                  title={<Phrase m='Common' p='advanced.search.filter.tag' />}
                  operator='and'
                  attribute='tags'
                  searchable
                  limit={10}
                />
              </div>
              {isFiltersOverlay &&
                <div class='instant-search__results-wrapper-filter-show-more-wrapper'>
                  <Button
                    cta
                    class='instant-search__results-wrapper-filter-show-more'
                    onClick={this.filtersOverlayHandler}
                  >
                    {<Phrase m='Forum' p='advanced.search.show.more.topics' count={{ number: totalTopicsAmount.toString() }} />}
                  </Button>
                </div>
              }
            </div>

            <div class='col col--main has--side search-topic-list-wrapper'>
              <CustomCurrentRefinements />

              <SearchResults
                isGuest={isGuest}
                communityId={this.communityId}
                attachmentCdn={attachmentCdn}
                defaultCdn={defaultCdn}
                enableLoader={enableLoader}
                isFederatedSearch={isFederatedSearch}
                isFederatedSalesforceSearch={isFederatedSalesforceSearch}
                pageCount={pageCount}
                loadNextPage={() => this.setState({ pageCount: pageCount + 1 })}
                loaderCallback={(enableLoader: boolean) => this.setState({ enableLoader: enableLoader })}
                totalTopicsAmount={this.totalTopicsAmount}
              />
            </div>
          </div>
        </InstantSearch>
      </div>
    )
  }
}

export default InstantSearchComponent
